import { InputRule, inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { Fragment, Slice } from 'prosemirror-model';
import { liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list';
import type { Transaction, Command } from 'prosemirror-state';
import { Plugin } from 'prosemirror-state';
import { Transform } from 'prosemirror-transform';
import type { EditorView } from 'prosemirror-view';
import { BehaviorSubject, Subject } from 'rxjs';

import { WysiwygShortcuts } from '@core/wysiwyg-legacy';

import { ChecklistService } from './checklist-service-public';
import type { ChecklistEvents } from './events';
import { ParagraphNode } from '../basic/nodes';
import type { ListItemKeymap } from '../list/list';
import type { ChecklistItemAttrs } from './nodes/checklist-item.node';
import { ChecklistItemNode } from './nodes/checklist-item.node';
import type { CreateChecklistItemNodeView } from './nodes/checklist-item.node-view';
import { ChecklistItemNodeView } from './nodes/checklist-item.node-view';
import { ChecklistNode } from './nodes/checklist.node';
import type { ChecklistSchema } from './nodes/types';
import { EditorMode } from '../../editor/types';
import type { EditorButton, RtPMPlugin } from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY, isMacOS, metaKey } from '../../helpers/utils';

const CHECKLIST_ITEM_REGEX = /^\s*(\[(\s*|x)\])\s/;

export class ChecklistPlugin implements RtPMPlugin {
  public api: {
    checklistService: ChecklistService;
  };
  public buttons = {
    toggleChecklist: <EditorButton>{
      icon: 'checklist',
      tooltipText: 'wysiwyg.checklist',
      tooltipShortcut: WysiwygShortcuts.Checklist(metaKey),
      isActive: (...args) => this.api.checklistService.isActive(...args),
      run: (state, dispatch) => {
        const getState = () => this.editorView.state;

        this.api.checklistService.toggle(getState, dispatch);
      },
    },
  };
  public eventStream$ = new Subject<ChecklistEvents>();

  private canToggleCheckbox$ = new BehaviorSubject(false);
  private editorView: EditorView;
  private pasteListPlugin = new Plugin({
    props: {
      transformPasted: slice => {
        let newContent = Fragment.from();
        slice.content.forEach((node, offset, index) => {
          const lastChild = newContent.childCount
            ? newContent.child(newContent.childCount - 1)
            : null;
          const isTextOnlyParagraph =
            ParagraphNode.is(node) &&
            !!node.childCount &&
            [...Array(node.childCount)].every((_, i) => node.child(i).isText);

          if (!isTextOnlyParagraph) {
            newContent = newContent.append(Fragment.from(node));
            return;
          }

          const nodeText = node.textBetween(0, node.content.size);
          newContent = CHECKLIST_ITEM_REGEX.test(nodeText)
            ? this.textNodeToCheckListItem(node, lastChild, newContent)
            : newContent.append(Fragment.from(node));
        });

        return new Slice(newContent, slice.openStart, slice.openEnd);
      },
    },
  });

  private checkListViewEdit = new Plugin({
    props: {
      nodeViews: {
        [ChecklistItemNode.nodeName]: (node, view, getPos) => {
          const defaultArgs = <const>[node, view, <() => number>getPos];
          if (this.checklistItemNodeViewFactory) {
            return this.checklistItemNodeViewFactory(
              ...defaultArgs,
              this.canToggleCheckbox$,
              this.eventStream$,
            );
          } else {
            return new ChecklistItemNodeView(
              ...defaultArgs,
              this.canToggleCheckbox$,
              this.eventStream$,
            );
          }
        },
      },
    },
  });

  constructor(private checklistItemNodeViewFactory?: CreateChecklistItemNodeView) {}

  public beforeInit(mode: EditorMode, schema: ChecklistSchema) {
    const { checklist, checklist_item, paragraph } = schema.nodes;
    this.api = {
      checklistService: new ChecklistService(
        checklist,
        checklist_item,
        paragraph,
        this.canToggleCheckbox$,
      ),
    };

    this.canToggleCheckbox$.next(EditorMode.Wysiwyg === mode);
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeViewPlugins() {
    return [this.checkListViewEdit];
  }

  public getNativeEditorPlugins(schema: ChecklistSchema) {
    return [
      this.pasteListPlugin,
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: keymap(this.getListContainersKeyBindings(schema)),
      },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: keymap(this.getChecklistItemKeyBindings(schema)),
      },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: inputRules({ rules: this.getInputRules(schema) }),
      },
    ];
  }

  private getListContainersKeyBindings(schema: ChecklistSchema) {
    const wrapInListChecklistKey = isMacOS ? 'Shift-Mod-9' : 'Shift-Ctrl-9';
    const getState = () => this.editorView.state;

    return <Record<string, Command>>{
      [wrapInListChecklistKey]: (state, dispatch) =>
        this.api.checklistService.toggle(getState, dispatch),
    };
  }

  private getChecklistItemKeyBindings(schema: ChecklistSchema) {
    const { checklist_item } = schema.nodes;
    const splitChecklistItem = splitListItem(checklist_item);
    const keymap: ListItemKeymap = {
      ['Mod-[']: liftListItem(checklist_item),
      ['Shift-Tab']: liftListItem(checklist_item),
      ['Mod-]']: sinkListItem(checklist_item),
      ['Tab']: sinkListItem(checklist_item),
      ['Enter']: (state, dispatch) => {
        const done = splitChecklistItem(state, dispatch);
        if (done) {
          const { doc, selection, tr } = this.editorView.state;
          const node = doc.nodeAt(selection.from - 2);
          if (node && ChecklistItemNode.is(node)) {
            tr.setNodeMarkup(selection.from - 2, undefined, <ChecklistItemAttrs>{ checked: false });
            this.editorView.dispatch(tr);
          }
        }
        return done;
      },
    };
    return <Record<string, Command>>(<unknown>keymap);
  }

  private getInputRules(schema: ChecklistSchema) {
    return [
      new InputRule(CHECKLIST_ITEM_REGEX, (state, match, start, end) => {
        const inputRule = wrappingInputRule(CHECKLIST_ITEM_REGEX, schema.nodes.checklist);
        const tr = <Transaction>(<any>inputRule).handler(state, match, start, end);
        tr.setNodeMarkup(tr.selection.from - 2, undefined, <ChecklistItemAttrs>{
          checked: 'x' === match[2],
        });
        return tr;
      }),
    ];
  }

  private textNodeToCheckListItem(
    textNode: ProsemirrorNode,
    lastChild: ProsemirrorNode | null,
    newContent: Fragment,
  ): Fragment {
    const { schema } = this.editorView.state;
    const nodeText = textNode.textBetween(0, textNode.content.size);
    const [allMatch, _, content] = nodeText.match(CHECKLIST_ITEM_REGEX)!;
    const itemText = nodeText.replace(allMatch, '').trim();
    const checked = content === 'x';

    const checkListItem = schema.nodes.checklist_item.createChecked(
      { checked },
      schema.nodes.paragraph.createChecked(null, schema.text(itemText)),
    );

    if (lastChild && ChecklistNode.is(lastChild)) {
      const tr = new Transform(lastChild);
      tr.insert(lastChild.content.size, checkListItem);
      return newContent.replaceChild(newContent.childCount - 1, tr.doc);
    } else {
      const list = schema.nodes.checklist.createChecked(null, [checkListItem]);
      return newContent.append(Fragment.from(list));
    }
  }
}
